import React from 'react';
import { useTranslation } from 'react-i18next';
import LoadingOverlay from '../LoadingOverlay';

export type ImagePosition = 'demo' | 'guide' | 'result';

export interface GuidedTourImageSlotProps {
  stepId: string;
  position: ImagePosition;
  alt?: string;
  className?: string;
  fallbackText?: string;
}

/**
 * 图片/GIF展示槽位组件
 * 
 * 图片加载路径格式：
 * /tour-images/{stepId}/{position}.gif 或 /tour-images/{stepId}/{position}.png
 * 
 * 示例：
 * - /tour-images/area-drawing/demo.gif (操作演示)
 * - /tour-images/area-drawing/guide.png (操作步骤说明)
 * - /tour-images/area-drawing/result.png (最终效果)
 */
const GuidedTourImageSlot: React.FC<GuidedTourImageSlotProps> = ({
  stepId,
  position,
  alt,
  className = '',
  fallbackText,
}) => {
  const { t } = useTranslation();

  const positionLabels: Record<ImagePosition, string> = {
    demo: t('tour.imageLabels.demo', '操作演示'),
    guide: t('tour.imageLabels.guide', '步骤说明'),
    result: t('tour.imageLabels.result', '最终效果'),
  };

  const imagePath = `/tour-images/${stepId}/${position}`;
  
  const [imageSrc, setImageSrc] = React.useState<string | null>(null);
  const [hasError, setHasError] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const checkImage = async () => {
      setIsLoading(true);
      setHasError(false);
      
      try {
        // 先尝试 gif 格式
        const gifPath = `${imagePath}.gif`;
        const gifResponse = await fetch(gifPath);
        if (gifResponse.ok) {
          setImageSrc(gifPath);
          setIsLoading(false);
          return;
        }
        
        // 再尝试 png 格式
        const pngPath = `${imagePath}.png`;
        const pngResponse = await fetch(pngPath);
        if (pngResponse.ok) {
          setImageSrc(pngPath);
          setIsLoading(false);
          return;
        }
        
        // 都找不到，设置错误状态
        setHasError(true);
        setIsLoading(false);
      } catch {
        setHasError(true);
        setIsLoading(false);
      }
    };

    checkImage();
  }, [imagePath]);

  return (
    <div
      className={`guided-tour-image-slot ${className}`}
      style={{
        width: '100%',
      }}
    >
      {/* 图片容器 - 16:9 比例 */}
      <div
        style={{
          width: '100%',
          paddingBottom: '56.25%', // 16:9 比例
          height: 0,
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {isLoading && (
          <LoadingOverlay text={t('tour.loading', '加载中...')} />
        )}

        {!isLoading && hasError && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9ca3af',
              fontSize: '12px',
              textAlign: 'center',
              padding: '8px',
            }}
          >
            {fallbackText || (
              <>
                <div style={{ marginBottom: '4px', opacity: 0.5 }}>
                  {/* 占位图标 */}
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                </div>
                <div className="text-xs">{positionLabels[position]}</div>
              </>
            )}
          </div>
        )}

        {!isLoading && imageSrc && !hasError && (
          <img
            src={imageSrc}
            alt={alt || `${positionLabels[position]} - ${stepId}`}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
            onError={() => setHasError(true)}
          />
        )}
      </div>
    </div>
  );
};

export default GuidedTourImageSlot;
